import { enumOptions, enumTypeName, quoteIdent, tableColumns, type CustomDriver, type EnumOption } from '@w/db-custom'
import type { PropertyKind } from './database-sql'
import { isPersonDefinition } from './person'

export type DatabaseProperty = { name: string; type: string; kind: PropertyKind | null; enumType?: string; target?: string; options: EnumOption[] }

/** SQL schema and native enum definitions are the only property catalog. */
export async function readDatabaseProperties(db: CustomDriver, table: string): Promise<DatabaseProperty[]> {
  const columns = await db.query<{ name: string; type: string; hidden: number }>(`PRAGMA table_xinfo(${quoteIdent(table)})`)
  const [schema] = await db.query<{ sql: string }>('SELECT sql FROM sqlite_schema WHERE type = \'table\' AND name = ? COLLATE NOCASE', [table])
  if (!schema) throw new Error('Database unavailable')
  const definitions = tableColumns(schema.sql)
  const foreignKeys = await db.query<{ from: string; table: string }>(`PRAGMA foreign_key_list(${quoteIdent(table)})`)
  const [typesTable] = await db.query<{ name: string }>("SELECT name FROM sqlite_schema WHERE name = '__turso_internal_types'")
  const types = typesTable ? await db.query<{ name: string; sql: string }>('SELECT name, sql FROM __turso_internal_types') : []
  return columns.filter(column => column.name !== 'id').map(column => {
    const declaration = definitions.find(item => item.name === column.name)
    const type = (declaration?.type ?? column.type).toLowerCase()
    const enumType = types.find(item => enumTypeName(item.sql).toLowerCase() === type.replace(/\[\]$/u, ''))
    const definition = declaration?.sql ?? ''
    let kind: PropertyKind | null = type === 'text' ? 'text' : type === 'real' || type === 'integer' ? 'number' : type === 'boolean' ? 'checkbox' : type === 'date' ? 'date' : type === 'timestamp' ? 'dateTime' : null
    if (enumType) kind = type.endsWith('[]') ? 'multiSelect' : 'select'
    if (/REFERENCES/iu.test(definition) || foreignKeys.some(key => key.from === column.name)) kind = 'relation'
    if (definition.includes("GLOB 'http://?*'")) kind = 'url'
    if (definition.includes("GLOB '?*@?*.?*'")) kind = 'email'
    if (definition.includes("GLOB '[+0-9(]*'")) kind = 'phone'
    if (isPersonDefinition(definition)) kind = 'person'
    if (column.hidden !== 0) kind = null
    return { name: column.name, target: foreignKeys.find(key => key.from === column.name)?.table, type: column.type, kind, enumType: enumType ? enumTypeName(enumType.sql) : undefined, options: enumType ? enumOptions(enumType.sql) : [] }
  })
}
